import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Switch, FormControlLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Divider, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent,
  Menu, ListItemIcon, ListItemText,
} from '@mui/material';
import { Close, Settings, Notifications, Add, Delete, Visibility, VisibilityOff, People as PeopleIcon, Phone, Business, Apartment, PhotoCamera as PhotoCameraIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import ContatosModal from '../../components/appcliente/ContatosModal';
import { configurationService } from '../../services/configurationService';
import ImageUploadCrop from '../../components/common/ImageUploadCrop';
import SysEditorWhatsapp from '../../components/syseditor/SysEditorWhatsapp';
import marketingMultinivelService from '../../services/marketingMultinivelService';

interface AppclienteNotificacao {
  id: number;
  titulo: string;
  descricao: string;
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente' | 'novidade';
  status: 'ativo' | 'cancelado';
  createdAt: string;
  updatedAt: string;
  createdBy?: number;
  updatedBy?: number;
  leituras?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} id={`central-tabpanel-${index}`} aria-labelledby={`central-tab-${index}`} {...other}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const a11yProps = (index: number) => ({
  id: `central-tab-${index}`,
  'aria-controls': `central-tabpanel-${index}`,
});

const ParametrosCentral: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isCadastroModalOpen, setIsCadastroModalOpen] = useState(false);
  const [novaNotificacao, setNovaNotificacao] = useState({
    titulo: '',
    descricao: '',
    prioridade: 'media' as 'baixa' | 'media' | 'alta' | 'urgente' | 'novidade',
    status: 'ativo' as 'ativo' | 'cancelado',
  });
  const [isSalvando, setIsSalvando] = useState(false);
  const [notificacoes, setNotificacoes] = useState<AppclienteNotificacao[]>([]);
  const [loadingNotificacoes, setLoadingNotificacoes] = useState(true);

  // Estados para imagem do dashboard
  const [imagemDashboard, setImagemDashboard] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  // Estados para os modais de contatos
  const [modalContatosOpen, setModalContatosOpen] = useState(false);
  const [tipoContatoSelecionado, setTipoContatoSelecionado] = useState<'canal_atendimento' | 'escritorios' | 'departamentos'>('canal_atendimento');

  // Estado da configuração de ocultar contratos cancelados
  const [ocultarContratosCancelados, setOcultarContratosCancelados] = useState(false);
  const [exigirAssinaturaContrato, setExigirAssinaturaContrato] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [updatingApp, setUpdatingApp] = useState(false);

  // Estado para visualização de indicações
  const [visualizacaoIndicacoes, setVisualizacaoIndicacoes] = useState<'arvore' | 'tabela'>('arvore');
  const [savingVisualizacao, setSavingVisualizacao] = useState(false);

  // Estado para status da central do cliente
  const [statusCentral, setStatusCentral] = useState<'ativo' | 'inativo' | 'manutencao'>('ativo');
  const [savingStatusCentral, setSavingStatusCentral] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  const token = localStorage.getItem('sysup_token');

  const handleAtualizarApp = async () => {
    setUpdatingApp(true);
    try {
      const response = await configurationService.updateAppClienteBranding();
      if (response.success) {
        toast.success(response.message || 'AppCliente atualizado com sucesso!');
      } else {
        toast.error(response.message || 'Erro ao atualizar AppCliente');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar AppCliente:', error);
      toast.error(error.message || 'Erro ao atualizar AppCliente');
    } finally {
      setUpdatingApp(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const handleOpenCadastroModal = () => {
    setIsCadastroModalOpen(true);
    setNovaNotificacao({ titulo: '', descricao: '', prioridade: 'media', status: 'ativo' });
  };

  const handleCloseCadastroModal = () => {
    setIsCadastroModalOpen(false);
    setNovaNotificacao({ titulo: '', descricao: '', prioridade: 'media', status: 'ativo' });
  };

  const carregarNotificacoes = async () => {
    try {
      setLoadingNotificacoes(true);
      if (!token) return toast.error('Você precisa estar logado para carregar notificações.');

      const response = await fetch(`${API_URL}/api/appcliente-notificacoes`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setNotificacoes(data.data.notificacoes);
      } else {
        toast.error('Erro ao carregar notificações cadastradas');
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      toast.error('Erro ao carregar notificações');
    } finally {
      setLoadingNotificacoes(false);
    }
  };

  const handleSalvarNotificacao = async () => {
    if (!novaNotificacao.titulo.trim() || !novaNotificacao.descricao.trim()) {
      return toast.error('Título e descrição são obrigatórios');
    }

    try {
      setIsSalvando(true);
      if (!token) return toast.error('Você precisa estar logado para salvar notificações.');

      const response = await fetch(`${API_URL}/api/appcliente-notificacoes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(novaNotificacao),
      });

      if (response.ok) {
        toast.success('Notificação cadastrada com sucesso!');
        handleCloseCadastroModal();
        carregarNotificacoes();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Erro ao salvar notificação');
      }
    } catch (error) {
      console.error('Erro ao salvar notificação:', error);
      toast.error('Erro ao salvar notificação');
    } finally {
      setIsSalvando(false);
    }
  };

  const handleAlterarStatus = async (id: number, novoStatus: 'ativo' | 'cancelado') => {
    try {
      if (!token) return toast.error('Você precisa estar logado para alterar notificações.');

      const response = await fetch(`${API_URL}/api/appcliente-notificacoes/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status: novoStatus }),
      });

      if (response.ok) {
        toast.success(`Status alterado para ${novoStatus}`);
        carregarNotificacoes();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Erro ao alterar status');
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status');
    }
  };

  const handleExcluirNotificacao = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta notificação?')) return;

    try {
      if (!token) return toast.error('Você precisa estar logado para excluir notificações.');

      const response = await fetch(`${API_URL}/api/appcliente-notificacoes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Notificação excluída com sucesso!');
        carregarNotificacoes();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || 'Erro ao excluir notificação');
      }
    } catch (error) {
      console.error('Erro ao excluir notificação:', error);
      toast.error('Erro ao excluir notificação');
    }
  };

  useEffect(() => {
    if (tabValue === 1) carregarNotificacoes();
    if (tabValue === 0) {
      loadImagemDashboard();
      loadConfigOcultarContratos();
      loadConfigExigirAssinatura();
      loadConfigVisualizacaoIndicacoes();
      loadConfigStatusCentral();
    }
  }, [tabValue]);

  const loadConfigOcultarContratos = async () => {
    try {
      const config = await configurationService.getConfigurationByKey('ocultar_contratos_cancelados');
      setOcultarContratosCancelados(config.valor === 'true' || config.valor === '1');
    } catch (error) {
      // Se a configuração não existir, mantemos o padrão (false)
      setOcultarContratosCancelados(false);
    }
  };

  const handleToggleOcultarContratos = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setOcultarContratosCancelados(newValue);
    setSavingConfig(true);
    try {
      await configurationService.upsertConfiguration(
        'ocultar_contratos_cancelados',
        String(newValue),
        { descricao: 'Ocultar contratos cancelados na central do assinante', categoria: 'central', tipo: 'boolean' }
      );
      toast.success('Configuração atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error('Erro ao atualizar a configuração.');
      // Reverter estado se falhar
      setOcultarContratosCancelados(!newValue);
    } finally {
      setSavingConfig(false);
    }
  };

  const loadConfigExigirAssinatura = async () => {
    try {
      const config = await configurationService.getConfigurationByKey('exigir_assinatura_contrato');
      setExigirAssinaturaContrato(config.valor === 'true' || config.valor === '1');
    } catch (error) {
      setExigirAssinaturaContrato(false);
    }
  };

  const handleToggleExigirAssinatura = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setExigirAssinaturaContrato(newValue);
    setSavingConfig(true);
    try {
      await configurationService.upsertConfiguration(
        'exigir_assinatura_contrato',
        String(newValue),
        { descricao: 'Exigir assinatura de contrato na central do assinante', categoria: 'central', tipo: 'boolean' }
      );
      toast.success('Configuração atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error('Erro ao atualizar a configuração.');
      setExigirAssinaturaContrato(!newValue);
    } finally {
      setSavingConfig(false);
    }
  };

  const loadConfigVisualizacaoIndicacoes = async () => {
    try {
      const config = await configurationService.getConfigurationByKey('visualizacao_indicacoes_appcliente');
      if (config && (config.valor === 'arvore' || config.valor === 'tabela')) {
        setVisualizacaoIndicacoes(config.valor as 'arvore' | 'tabela');
      } else {
        setVisualizacaoIndicacoes('arvore');
      }
    } catch (error) {
      console.error('Erro ao carregar configuração de visualização:', error);
      setVisualizacaoIndicacoes('arvore');
    }
  };

  const handleChangeVisualizacao = async (event: SelectChangeEvent) => {
    const newValue = event.target.value as 'arvore' | 'tabela';
    setVisualizacaoIndicacoes(newValue);
    setSavingVisualizacao(true);

    try {
      await configurationService.upsertConfiguration(
        'visualizacao_indicacoes_appcliente',
        newValue,
        { descricao: 'Modo de exibição das indicações no app do cliente (arvore ou tabela)', categoria: 'central', tipo: 'string' }
      );
      toast.success('Modo de visualização atualizado!');
    } catch (error) {
      console.error('Erro ao salvar visualização:', error);
      toast.error('Erro ao atualizar modo de visualização');
      // Reverter em caso de erro
      loadConfigVisualizacaoIndicacoes();
    } finally {
      setSavingVisualizacao(false);
    }
  };

  const loadConfigStatusCentral = async () => {
    try {
      const config = await configurationService.getConfigurationByKey('status_central_cliente');
      if (config && (config.valor === 'ativo' || config.valor === 'inativo' || config.valor === 'manutencao')) {
        setStatusCentral(config.valor as 'ativo' | 'inativo' | 'manutencao');
      } else {
        setStatusCentral('ativo');
      }
    } catch (error) {
      console.error('Erro ao carregar status da central:', error);
      setStatusCentral('ativo');
    }
  };

  const handleChangeStatusCentral = async (event: SelectChangeEvent) => {
    const newValue = event.target.value as 'ativo' | 'inativo' | 'manutencao';
    setStatusCentral(newValue);
    setSavingStatusCentral(true);

    try {
      await configurationService.upsertConfiguration(
        'status_central_cliente',
        newValue,
        { descricao: 'Status de funcionamento da Central do Cliente (ativo, inativo ou manutencao)', categoria: 'central', tipo: 'string' }
      );
      toast.success('Status da Central do Cliente atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar status da central:', error);
      toast.error('Erro ao atualizar status da central');
      loadConfigStatusCentral();
    } finally {
      setSavingStatusCentral(false);
    }
  };


  const loadImagemDashboard = async () => {
    try {
      const data = await configurationService.getAppClienteDashboardImg();
      if (data?.imagem) {
        setImagemDashboard(data.imagem);
      } else {
        setImagemDashboard(null);
      }
    } catch (error) {
      console.error('Erro ao carregar imagem do dashboard:', error);
    }
  };

  const handleImageSave = async (croppedImageBlob: Blob) => {
    setUploading(true);
    try {
      const extension = croppedImageBlob.type === 'image/png' ? 'png' :
        croppedImageBlob.type === 'image/webp' ? 'webp' : 'jpg';
      const file = new File([croppedImageBlob], `dashboard-img.${extension}`, {
        type: croppedImageBlob.type
      });
      const response = await configurationService.uploadAppClienteDashboardImg(file);
      if (response.success) {
        toast.success('Imagem enviada com sucesso!');
        await loadImagemDashboard();
      } else {
        toast.error(response.message || 'Erro ao fazer upload da imagem');
      }
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      toast.error('Erro ao fazer upload da imagem');
    } finally {
      setUploading(false);
      setUploadModalOpen(false);
    }
  };

  const handleRemoveImage = async () => {
    try {
      setUploading(true);
      const result = await configurationService.removeAppClienteDashboardImg();
      if (result.success) {
        toast.success('Imagem removida com sucesso!');
        setImagemDashboard(null);
      } else {
        toast.error(result.message || 'Erro ao remover imagem');
      }
    } catch (error: any) {
      console.error('Erro ao remover imagem:', error);
      toast.error('Erro ao remover imagem');
    } finally {
      setUploading(false);
      setMenuAnchor(null);
    }
  };

  const getDashboardImageUrl = () => {
    if (!imagemDashboard) return '';
    return configurationService.getAppClienteDashboardImgUrl(imagemDashboard);
  };

  const getStatusColor = (status: string) => status === 'ativo' ? 'success' : 'error';

  // Funções para abrir modais de contatos
  const handleOpenModalContatos = (tipo: 'canal_atendimento' | 'escritorios' | 'departamentos') => {
    setTipoContatoSelecionado(tipo);
    setModalContatosOpen(true);
  };

  const handleCloseModalContatos = () => {
    setModalContatosOpen(false);
  };

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Sub-configurações da Central"
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              minHeight: 40,
              fontSize: '0.8rem',
              padding: '6px 12px',
            }
          }}
        >
          <Tab
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Settings fontSize="small" />Geral</Box>}
            {...a11yProps(0)}
          />
          <Tab
            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Notifications fontSize="small" />Notificações</Box>}
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>

      {/* Aba Geral */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ p: 1 }}>
          {/* Botões de Gerenciamento de Contatos e Atualizar App */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Phone />}
                onClick={() => handleOpenModalContatos('canal_atendimento')}
              >
                Canais de Atendimento
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Business />}
                onClick={() => handleOpenModalContatos('escritorios')}
              >
                Escritórios
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Apartment />}
                onClick={() => handleOpenModalContatos('departamentos')}
              >
                Departamentos
              </Button>
            </Box>

            <Button
              variant="outlined"
              color="primary"
              startIcon={updatingApp ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={handleAtualizarApp}
              disabled={updatingApp}
              size="small"
            >
              {updatingApp ? 'Atualizando APP...' : 'Atualizar APP'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />


          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            {imagemDashboard ? (
              <img
                src={getDashboardImageUrl()}
                alt="Marketing Dashboard"
                style={{
                  maxWidth: 160,
                  maxHeight: 100,
                  cursor: 'pointer',
                  borderRadius: 4,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  objectFit: 'contain'
                }}
                onClick={(e) => setMenuAnchor(e.currentTarget)}
              />
            ) : (
              <Box
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  width: 160,
                  height: 80,
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  bgcolor: 'background.paper',
                  '&:hover': { borderColor: 'primary.main' }
                }}
              >
                <PhotoCameraIcon color="disabled" />
              </Box>
            )}
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">Imagem de Marketing do Dashboard</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Imagem exibida no Dashboard abaixo da fatura em aberto.
              </Typography>
              <Button size="small" variant="text" onClick={() => setUploadModalOpen(true)} sx={{ mt: 0.5 }}>
                {imagemDashboard ? 'Alterar Imagem' : 'Adicionar Imagem'}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Configuração de Visualização de Indicações */}
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Modo de Exibição das Indicações (App Cliente)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Escolha como o cliente verá sua rede de indicações no aplicativo.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="visualizacao-indicacoes-label">Modo de Exibição</InputLabel>
                  <Select
                    labelId="visualizacao-indicacoes-label"
                    value={visualizacaoIndicacoes}
                    label="Modo de Exibição"
                    onChange={handleChangeVisualizacao}
                    disabled={savingVisualizacao}
                  >
                    <MenuItem value="arvore">Visualização em Árvore (Gráfico)</MenuItem>
                    <MenuItem value="tabela">Visualização em Tabela (Lista)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          {/* Configuração de Status da Central do Cliente */}
          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Status da Central do Cliente</Typography>
                <Typography variant="body2" color="text.secondary">
                  Define a disponibilidade da central para os clientes (Ativo, Inativo ou Manutenção).
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="status-central-label">Status da Central</InputLabel>
                  <Select
                    labelId="status-central-label"
                    value={statusCentral}
                    label="Status da Central"
                    onChange={handleChangeStatusCentral}
                    disabled={savingStatusCentral}
                  >
                    <MenuItem value="ativo">Ativo</MenuItem>
                    <MenuItem value="inativo">Inativo</MenuItem>
                    <MenuItem value="manutencao">Manutenção</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
              Configurações de Exibição
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ocultarContratosCancelados}
                      onChange={handleToggleOcultarContratos}
                      disabled={savingConfig}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">
                        Ocultar Contratos Cancelados
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ao ativar, contratos com status "cancelado" não serão exibidos na seleção de contratos da central do assinante.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exigirAssinaturaContrato}
                      onChange={handleToggleExigirAssinatura}
                      disabled={savingConfig}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="500">
                        Exigir assinatura de Contrato
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Ao ativar, contratos não assinados exigirão a assinatura do cliente para que ele possa acessar a central.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </TabPanel>

      {/* Aba Notificações */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ p: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">Notificações</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleOpenCadastroModal}
            >
              Cadastrar
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Título</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Leituras</strong></TableCell>
                  <TableCell align="right"><strong>Ações</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingNotificacoes ? (
                  <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={20} /></TableCell></TableRow>
                ) : notificacoes.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center">Nenhuma notificação</TableCell></TableRow>
                ) : (
                  notificacoes.map((notificacao) => (
                    <TableRow key={notificacao.id}>
                      <TableCell>{notificacao.titulo}</TableCell>
                      <TableCell>
                        <Chip label={notificacao.status} size="small" color={getStatusColor(notificacao.status)} variant="outlined" />
                      </TableCell>
                      <TableCell>{notificacao.leituras || 0}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleAlterarStatus(notificacao.id, notificacao.status === 'ativo' ? 'cancelado' : 'ativo')}>
                          {notificacao.status === 'ativo' ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleExcluirNotificacao(notificacao.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </TabPanel>

      {/* Menus e Modais Adicionais */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setUploadModalOpen(true); setMenuAnchor(null); }}>
          <ListItemIcon><PhotoCameraIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Alterar Imagem</ListItemText>
        </MenuItem>
        {imagemDashboard && (
          <MenuItem onClick={handleRemoveImage} sx={{ color: 'error.main' }}>
            <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Remover Imagem</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <ImageUploadCrop
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onImageSave={handleImageSave}
        title="Upload de Imagem"
        //aspectRatio={9 / 16}
        maxWidth={720}
        //maxHeight={720}
        quality={0.8}
      />

      <ContatosModal
        open={modalContatosOpen}
        onClose={handleCloseModalContatos}
        tipo={tipoContatoSelecionado}
      />

      {/* Modal de Cadastro de Notificação */}
      <Dialog open={isCadastroModalOpen} onClose={handleCloseCadastroModal} maxWidth="md" fullWidth>
        <DialogTitle>Nova Notificação</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Título" size="small" value={novaNotificacao.titulo} onChange={(e) => setNovaNotificacao(prev => ({ ...prev, titulo: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <SysEditorWhatsapp
                label="Descrição"
                value={novaNotificacao.descricao}
                onChange={(value) => setNovaNotificacao(prev => ({ ...prev, descricao: value }))}
                placeholder="Digite a descrição da notificação..."
                rows={5}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Prioridade</InputLabel>
                <Select value={novaNotificacao.prioridade} label="Prioridade" onChange={(e: SelectChangeEvent) => setNovaNotificacao(prev => ({ ...prev, prioridade: e.target.value as any }))}>
                  <MenuItem value="baixa">Baixa</MenuItem>
                  <MenuItem value="media">Média</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                  <MenuItem value="novidade">Novidade</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={novaNotificacao.status} label="Status" onChange={(e: SelectChangeEvent) => setNovaNotificacao(prev => ({ ...prev, status: e.target.value as any }))}>
                  <MenuItem value="ativo">Ativo</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCadastroModal}>Cancelar</Button>
          <Button onClick={handleSalvarNotificacao} variant="contained" disabled={!novaNotificacao.titulo.trim() || isSalvando}>
            {isSalvando ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParametrosCentral;
